import React from 'react';
import { doc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { buildElevationProfile, elevacionEnDistancia } from '../utils/map';
import { calculaPrevision } from '../utils/time';
import { FirebaseContext } from '../index';
import AltimetriaChart from '../components/AltimetriaChart';
import MsgNetLoading from '../components/MsgNetLoading';

const TOP_N = 3;

class AltimetriaLEDScreenClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      competicion: null,
      controles: [],
      sectores: [],
      resultados: [],
      perfilElevacion: [],
      tick: 0
    };
    this.unlisten = {};
  }

  componentDidMount() {
    this.tickInterval = setInterval(() => this.setState({ tick: Date.now() }), 250);

    const { db, params } = this.props;
    const competicionId = parseInt(params.competicionId, 10);

    const competicionRef = doc(db, 'competicion', competicionId.toString());
    this.unlisten.competicion = onSnapshot(competicionRef, (snapshot) => {
      let competicion = snapshot.exists() ? snapshot.data() : null;
      if (competicion) competicion.id = snapshot.id;
      this.setState({ competicion }, this._recalcularPerfil);
    });

    const controlesQuery = query(
      collection(db, 'control'),
      where('competicion_id', '==', competicionId),
      where('visible', '==', true),
      orderBy('distancia', 'asc')
    );
    this.unlisten.controles = onSnapshot(controlesQuery, (snapshot) => {
      const controles = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      this.setState({ controles });
    });

    const sectoresQuery = query(
      collection(db, 'sector'),
      where('competicion_id', '==', competicionId),
      orderBy('inicio', 'asc')
    );
    this.unlisten.sectores = onSnapshot(sectoresQuery, (snapshot) => {
      const sectores = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      this.setState({ sectores });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { db, params } = this.props;

    if (prevState.controles !== this.state.controles &&
      this.state.controles.length > 0 && !this.unlisten.resultados) {
      const competicionId = parseInt(params.competicionId, 10);

      const resultadosQuery = query(
        collection(db, 'resultado'),
        where('competicion_id', '==', competicionId)
      );
      this.unlisten.resultados = onSnapshot(resultadosQuery, (snapshot) => {
        const resultados = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        this.setState({ resultados });
      });
    }
  }

  componentWillUnmount() {
    if (this.tickInterval) clearInterval(this.tickInterval);
    Object.values(this.unlisten).forEach(fn => fn && fn());
  }

  _recalcularPerfil = () => {
    const { competicion } = this.state;
    if (!competicion || !competicion.mapa_geojson) return;

    const data = JSON.parse(competicion.mapa_geojson);
    const coords = data.features[0].geometry.coordinates;
    const perfilElevacion = buildElevationProfile(coords);

    this.setState({ perfilElevacion });
  };

  render() {
    const { competicion, controles, sectores, resultados, perfilElevacion } = this.state;

    if (!competicion || !controles.length || perfilElevacion.length < 2) {
      return (
        <div className="min-h-screen grid place-items-center bg-black">
          <MsgNetLoading />
        </div>
      );
    }

    const totalKm = perfilElevacion[perfilElevacion.length - 1].distanciaKm;

    const conPrevision = resultados
      .filter(r => !r.descalificado && !r.retirado && !r.abandono)
      .map(r => ({ ...r, prevision: calculaPrevision(competicion, controles, sectores, r) }))
      .filter(r => r.prevision && typeof r.prevision.porcentaje === 'number' && !isNaN(r.prevision.porcentaje));

    // Cuenta a los que ya terminaron (fuera del grafico) para desplazar
    // la numeracion del resto - si el 1o real ya entro en meta, el que
    // ahora va primero entre los que siguen corriendo debe verse como
    // 2o, no como un "1o" nuevo.
    const finishedCount = conPrevision.filter(r => r.prevision.porcentaje >= 100).length;

    const primeros = conPrevision
      .filter(r => r.prevision.porcentaje < 100)
      .sort((a, b) => b.prevision.porcentaje - a.prevision.porcentaje)
      .slice(0, TOP_N)
      .map((r, idx) => ({
        dorsal: r.dorsal,
        nombre: r.nombre,
        apellidos: r.apellidos,
        porcentaje: r.prevision.porcentaje,
        position: finishedCount + idx + 1,
        elevacion: elevacionEnDistancia(perfilElevacion, (r.prevision.porcentaje / 100) * totalKm)
      }));

    return (
      <div className="w-screen h-screen bg-black">
        <AltimetriaChart perfilElevacion={perfilElevacion} primeros={primeros} />
      </div>
    );
  }
}

function AltimetriaLEDScreen() {
  const params = useParams();
  const { db } = React.useContext(FirebaseContext);
  return <AltimetriaLEDScreenClass params={params} db={db} />;
}

export default AltimetriaLEDScreen;
